import { useState, useCallback } from 'react'
import { supabase } from '../api/supabase'
import type { PoaBadge } from '../lib/poaStatus'

export interface EcustomsVariant {
  pva: boolean
  applicable: boolean
  csvContent: string | null
  // Z73.7: structured form of csvContent -- one Record<CDS header, cell text> per line, what the
  // editable preview grid actually edits.
  rows: Record<string, string>[] | null
}

export interface EcustomsVariantResult extends EcustomsVariant {
  reference: string | null
  statusCode: string | null
  statusDescription: string | null
  status: 'accepted' | 'rejected' | 'error' | 'not_applicable'
  errorMsg?: string
}

// Z73.3: one shared checklist for the whole job (CI data / EORI / addresses / ports / line
// items) -- see submit-ecustoms/index.ts's buildValidationChecklist for the exact rules.
export interface EcustomsChecklistItem {
  key: string
  label: string
  passed: boolean
  // Z73.6 revised: an unpassed "warning" item (POA expiring soon, borderline company-name match)
  // is shown but never blocks generation/submission -- only an unpassed item with no severity or
  // severity "error" does.
  severity?: 'error' | 'warning'
  detail?: string
}

export interface EcustomsPreview {
  success: boolean
  preview: true
  job: {
    job_ref: string
    importer_id: string | null
    importer_name: string | null
    importer_eori: string | null
    consignor_name: string | null
    origin_port_locode: string | null
    destination_port_locode: string | null
    awb_or_bl: string | null
    // Z92: raw customer-profile PVA flag (null/true/false) -- reference only, never pre-selects.
    importer_pva_flag?: boolean | null
    transport_mode?: string | null
    // The billing customer's default for paying duty via CY UK's DAN, and whose record it came from (null = unknown).
    dan_default?: boolean | null
    dan_default_source?: string | null
    // Z92: AA.22 arrival transport ID last entered for this job (vehicle registration).
    inland_transport_id?: string | null
  }
  hasCiData: boolean
  checklist: EcustomsChecklistItem[]
  variants: { pva: EcustomsVariant; nonPva: EcustomsVariant }
  // Z73.7: shared between both variants (useCyukDan is a job-level flag, not per-PVA) -- the
  // editable grid's column set. itemIds[i] is which invoice_items rows fed pivot/grid row i,
  // aligned with variants.pva.rows[i] / variants.nonPva.rows[i] (both built from the same pivot,
  // same order) -- empty array means the row can't be attributed to source data (shouldn't
  // happen for an unedited preview, but a client-added row starts this way too).
  headers: string[]
  itemIds: string[][]
  // Z73.6 revised: the importer's POA document (if one was found for their EORI), so staff can
  // quickly open it to check without leaving this page.
  poaDriveLink: string | null
  // Z73.8 Bug 2: the actual computed badge, not just checklist text -- lets the UI render a real
  // PoaStatusBadge next to the importer name in the Job Summary block, same as Gary asked for.
  poaBadge: PoaBadge
  environment: string
  error?: string
}

export interface EcustomsSubmitResult {
  success: boolean
  checklist?: EcustomsChecklistItem[]
  variants?: { pva: EcustomsVariantResult; nonPva: EcustomsVariantResult }
  error?: string
}

// Z73.7: what "Confirm & Generate Files" (and the edited-rows path through "submit") sends --
// cells only per row (rows[i].itemIds is split out into item_ids so the wire format matches what
// submit-ecustoms/index.ts expects; see ECustomsPreview.tsx's GridRow for the combined client shape).
export interface EditedDeclarationPayload {
  headers: string[]
  pvaRows: Record<string, string>[]
  nonPvaRows: Record<string, string>[]
  itemIds: string[][]
  overrideScope: 'submission_only' | 'saved_to_source'
}

export interface EcustomsGenerateResult {
  success: boolean
  batchId?: string
  pva?: { applicable: boolean; csvContent: string | null }
  nonPva?: { applicable: boolean; csvContent: string | null }
  savedToSource?: { updated: number; skipped: { row: number; reason: string }[] }
  rowErrors?: { variant: 'pva' | 'nonPva'; row: number; field: string }[]
  error?: string
}

// Z73.22: one CSV row's pre-serialized data -- matches cdsDeclarationBuilder.ts's
// DeclarationStructuredRow exactly (that's the Deno-side source of truth this mirrors).
export interface DeclarationStructuredRow {
  rowType: string
  label: string
  values: Record<string, string>
}

export type PvaMode = 'PVA' | 'NON_PVA'
export type DanMode = 'DAN' | 'NO_DAN'

export type DeclType = 'IMD' | 'IMA'
export type LoadType = 'FCL' | 'LCL' | 'AIR'
// The pre-submit checklist's decisions. null = not chosen yet (a decision is never assumed).
export interface V2Decisions {
  pvaMode: PvaMode | null
  danMode: DanMode | null
  declType: DeclType | null
  declarantRep: '2' | '3' | null
  loadType: LoadType | null
  arrivalId: string
  transitMrn: string
  vatAdjOverride: number | null
}
export interface ReviewItem { id: string; label: string; value: string }
export interface PrecheckCheck { id: string; level: 'red' | 'yellow'; message: string }
export interface VatAdjustmentInfo {
  applicable: boolean; grossKg: number; amount: number; manual: boolean
  calculated: { amount: number; currency: 'GBP'; formula: string; minApplied: boolean }
}
export interface PrecheckResult {
  success: boolean
  missing?: string[]
  blocker?: string            // the builder refused (e.g. Incoterm not set) -- shown inside the checklist, never as a dead end
  review?: ReviewItem[]
  checks?: PrecheckCheck[]
  vatAdjustment?: VatAdjustmentInfo
  defaults?: { declarantRep: '2' | '3'; loadType: LoadType | null; danDefault: boolean | null; danDefaultSource: string | null }
  error?: string
}

function decisionsBody(d: V2Decisions) {
  return {
    pva_mode: d.pvaMode, dan_mode: d.danMode, decl_type: d.declType, declarant_rep: d.declarantRep, load_type: d.loadType,
    arrival_transport_id: d.arrivalId, transit_mrn: d.transitMrn || null, vat_adjustment_override: d.vatAdjOverride,
  }
}

export interface EcustomsGenerateV2Result {
  success: boolean
  pvaMode?: PvaMode
  danMode?: DanMode
  decisions?: { pvaMode: PvaMode; danMode: DanMode; declType: DeclType; declarantRep: '2' | '3'; loadType: LoadType; arrivalId: string; transitMrn: string | null; vatAdjOverride: number | null }
  review?: ReviewItem[]
  checks?: PrecheckCheck[]
  vatAdjustment?: VatAdjustmentInfo
  arrivalTransportId?: string
  submissionId?: string | null
  filename?: string
  csvContent?: string
  structuredRows?: DeclarationStructuredRow[]
  soapXml?: string | null
  endpointUrl?: string
  credentialsConfigured?: boolean
  credentialsError?: string | null
  error?: string
}

export interface EcustomsSubmission {
  id: string
  job_id: string
  submission_type: string
  environment: string
  pva_used: boolean | null
  // Z92: true only when pva_used was derived from the file's actual DT rows; older rows carry a
  // hard-coded false and must not show a PVA tag.
  pva_from_csv?: boolean
  reference: string | null
  status_code: string | null
  status_description: string | null
  status: string
  submitted_at: string | null
  error_msg: string | null
  created_at: string
  batch_id: string | null
  edited_rows: Record<string, string>[] | null
  override_scope: string | null
  // Z73.16
  invoice_id: string | null
  invoiced_at: string | null
  invoice?: { invoice_number: string }[] | null
  // PR-C / PR-D: the checklist decisions this file was built from, and who / what (null on older rows)
  generated_by_email?: string | null
  submitted_by_email?: string | null
  file_name?: string | null
  dan_used?: boolean | null
  decl_type?: string | null
  declarant_rep?: string | null
  load_type?: string | null
  vat_adjustment_amount?: number | null
  vat_adjustment_manual?: boolean | null
}

// The three archive columns, fetched only when a history row is opened (they are large).
export interface SubmissionArchive {
  request_csv: string | null
  request_xml: string | null
  response_xml: string | null
}

export interface GenerateInvoiceDraftResult {
  success: boolean
  invoiceId?: string
  invoiceNumber?: string
  error?: string
}

// Z73 / Z73.2 / Z73.4 / Z73.7: thin client for the submit-ecustoms edge function -- see that
// function's own header comments for why this follows the CSV-in-SOAP shape from
// ECUSTOMS_INTEGRATION_BRIEF.md rather than a generic XML declaration, and why it always
// builds/submits BOTH a PVA and non-PVA variant per job rather than a toggle.
//
// Z73.4: previewLoading/submitting are two SEPARATE flags (previously one shared `loading`) --
// sharing one flag meant the initial preview fetch on page load (previewDeclaration) and an
// actual Submit click (submitDeclaration) both drove the same boolean, so the Submit button's
// "Submitting…" label lit up on page load before anyone clicked anything. Every returned
// function is now wrapped in useCallback with a stable (empty) dependency array -- previously
// each one was a fresh closure on every render, which made a `useEffect([..., previewDeclaration])`
// in the consuming component re-fire every time `loading` changed state, calling
// previewDeclaration again, causing loading to flip again, causing another re-render... an
// infinite re-fetch loop hammering this edge function continuously, which is almost certainly
// why "Submitting…" appeared permanently stuck rather than just wrongly-initialized.
export function useECustoms() {
  const [previewLoading, setPreviewLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [generating, setGenerating] = useState(false)

  const callFunction = useCallback(async <T,>(body: Record<string, unknown>): Promise<T> => {
    const { data: sessionData } = await supabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token || ''
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-ecustoms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    })
    return response.json()
  }, [])

  const previewDeclaration = useCallback(async (jobId: string): Promise<EcustomsPreview> => {
    setPreviewLoading(true)
    try {
      return await callFunction<EcustomsPreview>({ job_id: jobId, action: 'preview' })
    } finally {
      setPreviewLoading(false)
    }
  }, [callFunction])

  // Z73.7: "Confirm & Generate Files" -- re-validates and builds the final CSVs from the
  // staff-edited grid, writes the audit rows (status='generated'), and (per overrideScope)
  // optionally writes clean 1:1 rows back to their source invoice_items.
  const generateDeclaration = useCallback(async (jobId: string, edited: EditedDeclarationPayload): Promise<EcustomsGenerateResult> => {
    setGenerating(true)
    try {
      return await callFunction<EcustomsGenerateResult>({
        job_id: jobId, action: 'generate',
        headers: edited.headers, pva_rows: edited.pvaRows, nonPva_rows: edited.nonPvaRows,
        item_ids: edited.itemIds, override_scope: edited.overrideScope,
      })
    } finally {
      setGenerating(false)
    }
  }, [callFunction])

  // Z73.5: 'uat' is the only environment ECustomsPreview's Submit button ever sends -- there is
  // no PRODUCTION submit wired up yet (see submit-ecustoms/index.ts's own comment on
  // loadCredentials) -- forcing it here (rather than leaving the edge function's ECUSTOMS_ENV
  // secret to decide) means the button labeled "Submit to Descartes UAT" can never silently hit
  // production just because that secret happens to be misconfigured.
  // Z73.7: edited is optional but always passed once the grid step exists -- what Descartes
  // actually receives must be what staff confirmed, not the stale auto-computed preview.
  const submitDeclaration = useCallback(async (
    jobId: string, submissionType?: string, environment?: 'uat', edited?: EditedDeclarationPayload
  ): Promise<EcustomsSubmitResult> => {
    setSubmitting(true)
    try {
      return await callFunction<EcustomsSubmitResult>({
        job_id: jobId, action: 'submit', submission_type: submissionType, environment,
        ...(edited ? { headers: edited.headers, pva_rows: edited.pvaRows, nonPva_rows: edited.nonPvaRows } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }, [callFunction])

  const getSubmissionHistory = useCallback(async (jobId: string): Promise<EcustomsSubmission[]> => {
    const base = 'id, job_id, submission_type, environment, pva_used, reference, status_code, status_description, status, submitted_at, error_msg, created_at, batch_id, edited_rows, override_scope, pva_from_csv, invoice_id, invoiced_at, invoice:invoices(invoice_number)'
    const full = `${base}, generated_by_email, submitted_by_email, file_name, dan_used, decl_type, declarant_rep, load_type, vat_adjustment_amount, vat_adjustment_manual`
    let res = await supabase.from('ecustoms_submissions').select(full).eq('job_id', jobId).order('created_at', { ascending: false })
    if (res.error) {
      // The newer columns may not exist yet (migrations not run): fall back so the history never silently vanishes.
      console.error('[eCustoms] submission history: full select failed, falling back:', res.error.message)
      res = await supabase.from('ecustoms_submissions').select(base).eq('job_id', jobId).order('created_at', { ascending: false }) as typeof res
      if (res.error) throw new Error(`Could not load the submission history: ${res.error.message}`)
    }
    return (res.data as unknown as EcustomsSubmission[]) || []
  }, [])

  // What was ACTUALLY sent / received for one submission, exactly as stored (never regenerated from current data).
  const getSubmissionArchive = useCallback(async (submissionId: string): Promise<SubmissionArchive> => {
    const { data, error } = await supabase.from('ecustoms_submissions').select('request_csv, request_xml, response_xml').eq('id', submissionId).maybeSingle()
    if (error) throw new Error(error.message)
    return { request_csv: data?.request_csv ?? null, request_xml: data?.request_xml ?? null, response_xml: data?.response_xml ?? null }
  }, [])

  // Z73.16 Part 4: separate edge function (not submit-ecustoms) -- turns the job's most
  // recently generated declaration batch into a DRAFT sales invoice.
  const generateInvoiceDraft = useCallback(async (jobId: string): Promise<GenerateInvoiceDraftResult> => {
    const { data: sessionData } = await supabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token || ''
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-invoice-draft-from-ecustoms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ job_id: jobId }),
    })
    return response.json()
  }, [])

  // Z73.7: "Copy from previous submission" -- the most recent generated batch from another job
  // for the SAME importer (a repeat shipper's next job is the "similar job structure" case Gary
  // described), so staff can start from a known-good declaration instead of from scratch.
  const findPreviousBatch = useCallback(async (importerId: string, excludeJobId: string): Promise<{
    batchId: string; jobRef: string; pvaRows: Record<string, string>[] | null; nonPvaRows: Record<string, string>[] | null
  } | null> => {
    const { data: sameImporterJobs } = await supabase.from('jobs').select('id, job_ref').or(`importer_id.eq.${importerId},hbl_importer_id.eq.${importerId}`).neq('id', excludeJobId)  // the declared importer can be the house bill's
    const jobIds = (sameImporterJobs || []).map(j => j.id)
    if (jobIds.length === 0) return null

    const { data: rows } = await supabase
      .from('ecustoms_submissions')
      .select('job_id, batch_id, pva_used, edited_rows, created_at')
      .in('job_id', jobIds)
      .eq('status', 'generated')
      .not('batch_id', 'is', null)
      .order('created_at', { ascending: false })
      .limit(20)
    if (!rows || rows.length === 0) return null

    const latestBatchId = rows[0].batch_id as string
    const batchRows = rows.filter(r => r.batch_id === latestBatchId)
    const jobRef = sameImporterJobs?.find(j => j.id === rows[0].job_id)?.job_ref || ''
    return {
      batchId: latestBatchId,
      jobRef,
      pvaRows: (batchRows.find(r => r.pva_used === true)?.edited_rows as Record<string, string>[] | undefined) || null,
      nonPvaRows: (batchRows.find(r => r.pva_used === false)?.edited_rows as Record<string, string>[] | undefined) || null,
    }
  }, [])

  // Z73.18 Phase 2: the real v1.8.3 multi-row-type generator -- separate from generateDeclaration
  // above (which still drives the existing editable-grid flat-pivot CSV). Download-only: never
  // reaches Descartes, just builds the CSV (and SOAP XML, if DESCARTES_* secrets happen to be
  // configured) so Gary can verify the new format against UAT manually before Phase 3 wires up a
  // live submit.
  const [generatingV2, setGeneratingV2] = useState(false)
  const generateDeclarationV2 = useCallback(async (jobId: string, d: V2Decisions): Promise<EcustomsGenerateV2Result> => {
    setGeneratingV2(true)
    try {
      return await callFunction<EcustomsGenerateV2Result>({ job_id: jobId, action: 'generate_v2', ...decisionsBody(d) })
    } finally {
      setGeneratingV2(false)
    }
  }, [callFunction])

  // Dry run of generate_v2 for the checklist: builds the would-be file in memory (nothing saved) and returns the review
  // values and checks READ FROM THAT FILE, plus which decisions are still open.
  const precheckV2 = useCallback(async (jobId: string, d: V2Decisions): Promise<PrecheckResult> => {
    return await callFunction<PrecheckResult>({ job_id: jobId, action: 'precheck', ...decisionsBody(d) })
  }, [callFunction])

  // Z92: sends the v2 file generate_v2 produced to Descartes UAT. Credentials are assembled
  // server-side only; the response comes back raw (unparsed).
  const submitDeclarationV2 = useCallback(async (
    jobId: string, submissionId: string, filename: string, pvaMode: PvaMode, danMode: DanMode
  ): Promise<{ success: boolean; submissionId?: string; httpStatus?: number | null; pvaMode?: PvaMode; responseXml?: string; error?: string }> => {
    setSubmitting(true)
    try {
      return await callFunction({ job_id: jobId, action: 'submit_v2', submission_id: submissionId, filename, environment: 'uat', pva_mode: pvaMode, dan_mode: danMode })
    } finally {
      setSubmitting(false)
    }
  }, [callFunction])

  return {
    submitDeclarationV2, precheckV2,
    previewLoading, submitting, generating, generatingV2,
    previewDeclaration, submitDeclaration, generateDeclaration, getSubmissionHistory, getSubmissionArchive, findPreviousBatch,
    generateInvoiceDraft, generateDeclarationV2,
  }
}
